import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import { formatAuthError } from "../lib/authErrors";

export function LoginScreen({ onRegister }: { onRegister: () => void }) {
  const { login, requestLoginEmailCode, loginWithEmailCode } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState<"password" | "code">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "password") {
        await login(email.trim(), password);
      } else {
        await loginWithEmailCode(email.trim(), emailCode.trim());
      }
    } catch (err) {
      setError(formatAuthError(err, t, t("login.failed")));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    setCodeBusy(true);
    setError(null);
    setCodeSent(false);
    try {
      await requestLoginEmailCode(email.trim());
      setCodeSent(true);
    } catch (err) {
      setError(formatAuthError(err, t, t("login.failed")));
    } finally {
      setCodeBusy(false);
    }
  }

  const canSubmit = mode === "password" ? Boolean(email.trim() && password) : Boolean(email.trim() && emailCode.trim());

  return (
    <div className="login-wrap">
      <section className="auth-card">
      <h1 className="title auth-brand">{t("brand")}</h1>
      <p className="subtitle">{t("login.subtitle")}</p>

      {error ? <div className="error-banner">{error}</div> : null}

      <form onSubmit={submit}>
        <div className="auth-mode-tabs" role="tablist" aria-label="Login method">
          <button
            type="button"
            className={mode === "password" ? "active" : ""}
            onClick={() => setMode("password")}
            disabled={busy}
          >
            {t("login.passwordMode")}
          </button>
          <button type="button" className={mode === "code" ? "active" : ""} onClick={() => setMode("code")} disabled={busy}>
            {t("login.codeMode")}
          </button>
        </div>

        <label className="label" htmlFor="email">
          {t("login.email")}
        </label>
        <input
          id="email"
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={busy}
        />

        {mode === "password" ? (
          <>
            <label className="label" htmlFor="password">
              {t("login.password")}
            </label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-secondary auth-code-button"
              onClick={() => void sendCode()}
              disabled={busy || codeBusy || !email.trim()}
            >
              {codeBusy ? <span className="spinner inline" /> : t("login.sendCode")}
            </button>
            {codeSent ? <p className="muted-inline auth-code-status">{t("login.codeSent")}</p> : null}
            <label className="label" htmlFor="login-email-code">
              {t("login.emailCode")}
            </label>
            <input
              id="login-email-code"
              className="input"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder={t("login.codePlaceholder")}
              required
              disabled={busy}
            />
          </>
        )}

        <button type="submit" className="btn-primary" disabled={busy || !canSubmit}>
          {busy ? <span className="spinner inline" /> : t("login.submit")}
        </button>
      </form>

      <p className="switch-line">
        {t("login.noAccount")}{" "}
        <button type="button" className="link-btn" onClick={onRegister}>
          {t("login.createOne")}
        </button>
      </p>
      </section>
    </div>
  );
}
